import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { AdminScreenKey } from '../types/admin-roles';
import FinanceDashboardPage from './FinanceDashboardPage';
import ReportsTab from './finance/ReportsTab';
import PayoutsTab from './finance/PayoutsTab';
import OverviewTab from './finance/OverviewTab';
import ProofsTab from './finance/ProofsTab';
import ConfigurationTab from './finance/ConfigurationTab';
import DeliveryTab from './finance/DeliveryTab';

/**
 * Spec 053 §1.7 / §4 — the finance workspace shell. Tab lives in `?tab=` so links deep-link and
 * the browser back button works; Overview is the first tab and the default (Flow C2 entry);
 * `phase` marks a tab whose body has not shipped yet: it renders disabled and falls back to
 * Overview. Nothing carries it today — Configuration (Spec 053 Flow F6) shipped with Phase 3.
 *
 * #1113 — each tab names the screen it needs: Configuration is `finance-config` (service fee, Tabby,
 * margin, promotions cap), every other tab `finance`, including the new Delivery & Recovery tab
 * (delivery fee, recovery providers). Only the tabs the admin holds are shown; a
 * requested tab the admin cannot open falls back to the first visible one (no error page).
 */
export const FINANCE_TABS: { key: string; label: string; screen: AdminScreenKey; phase?: 2 | 3; badge?: string }[] = [
  { key: 'overview', label: 'Overview', screen: 'finance' },
  { key: 'dashboard', label: 'Dashboard', screen: 'finance' },
  { key: 'reports', label: 'Reports', screen: 'finance' },
  { key: 'proofs', label: 'Proofs', screen: 'finance' },
  { key: 'payouts', label: 'Stripe Payouts', screen: 'finance' },
  { key: 'configuration', label: 'Configuration', screen: 'finance-config' },
  { key: 'delivery', label: 'Delivery & Recovery', screen: 'finance', badge: 'New' },
];

export default function FinancePage() {
  const { hasScreen } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  const TABS = FINANCE_TABS.filter((t) => hasScreen(t.screen));
  const active = TABS.find((t) => t.key === requested && !t.phase)?.key ?? TABS.find((t) => !t.phase)?.key ?? null;

  const selectTab = (key: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    setSearchParams(next, { replace: true });
  };

  return (
    <div>
      <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--admin-text)', margin: '0 0 1rem' }}>Finance</h2>

      {/* Same tab treatment as SettlementsPage: navy text, yellow underline. */}
      <div
        className="admin-tabs"
        role="tablist"
        aria-label="Finance"
        style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem', borderBottom: '1px solid var(--admin-border, #e5e7eb)', overflowX: 'auto' }}
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            id={`finance-tab-${t.key}`}
            aria-controls="finance-tabpanel"
            aria-selected={active === t.key}
            disabled={!!t.phase}
            title={t.phase ? `Coming with Phase ${t.phase}` : undefined}
            onClick={() => selectTab(t.key)}
            style={{
              background: 'none', border: 'none', cursor: t.phase ? 'not-allowed' : 'pointer', padding: '0.625rem 1rem', whiteSpace: 'nowrap',
              fontFamily: 'inherit', fontSize: '0.875rem',
              fontWeight: active === t.key ? 600 : 500,
              color: active === t.key ? '#030c54' : t.phase ? '#b8bfcc' : 'var(--admin-text-muted, #6b7280)',
              borderBottom: active === t.key ? '2px solid #fff200' : '2px solid transparent',
              display: 'inline-flex', alignItems: 'center', gap: '0.375rem', minHeight: 44,
            }}
          >
            {t.label}
            {t.badge && <span className="admin-badge new">{t.badge}</span>}
            {t.phase && (
              <span style={{ fontSize: '0.6875rem', marginLeft: '0.35rem', fontWeight: 400 }}>
                (Phase {t.phase})
              </span>
            )}
          </button>
        ))}
      </div>

      <div role="tabpanel" id="finance-tabpanel" aria-labelledby={active ? `finance-tab-${active}` : undefined}>
        {active === 'overview' && <OverviewTab />}
        {active === 'dashboard' && <FinanceDashboardPage />}
        {active === 'reports' && <ReportsTab />}
        {active === 'proofs' && <ProofsTab />}
        {active === 'payouts' && <PayoutsTab />}
        {active === 'configuration' && <ConfigurationTab />}
        {active === 'delivery' && <DeliveryTab />}
      </div>
    </div>
  );
}
